/* Presentational pieces shared by every grievance screen.

   Kept in one file (the way AssetRequestPieces.js is) so the employee view and
   the handler view render an identical record identically — a status pill that
   means one thing on one screen and another elsewhere is how people stop
   trusting the queue. */
import { AlertTriangle, Clock, EyeOff, Lock, Paperclip, ShieldAlert } from "lucide-react";

import { API_URL, GRIEVANCE_ATTACHMENT_DOWNLOAD } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { truncateText } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";
import {
    AUDIT_ACTION_LABELS,
    displayEscalationRung,
    ESCALATION_LEVEL_LABELS,
    GRIEVANCE_PRIORITY_CHIP,
    GRIEVANCE_STATUS_CHIP,
    GRIEVANCE_STATUS_LABELS,
    RESOLUTION_TYPE_LABELS,
} from "../../constants/grievance";

/* ------------------------------- formatting ------------------------------- */

export const fmtDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const fmtDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return `${fmtDate(value)}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
};

/* Relative wording for the SLA countdown. Hours below a day read better than
   "0.4 days", which is why this is not a plain day count. */
export const fmtRemaining = (hours) => {
    if (hours === null || hours === undefined) return null;
    const abs = Math.abs(hours);
    const unit = abs < 24
        ? `${Math.round(abs)} hr${Math.round(abs) === 1 ? "" : "s"}`
        : `${Math.round(abs / 24)} day${Math.round(abs / 24) === 1 ? "" : "s"}`;
    return hours < 0 ? `${unit} overdue` : `${unit} left`;
};

/* --------------------------------- badges --------------------------------- */

const chip = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap";

export const StatusBadge = ({ status, resolutionType }) => {
    const outcome = (status === "resolved" || status === "closed")
        ? (RESOLUTION_TYPE_LABELS[resolutionType] || resolutionType)
        : null;
    return (
        <span className={cn(chip, GRIEVANCE_STATUS_CHIP[status] || GRIEVANCE_STATUS_CHIP.submitted)}>
            {outcome || GRIEVANCE_STATUS_LABELS[status] || status || "—"}
        </span>
    );
};

export const PriorityBadge = ({ priority }) => (
    <span className={cn(chip, GRIEVANCE_PRIORITY_CHIP[priority] || GRIEVANCE_PRIORITY_CHIP.medium)}>
        {(priority || "medium").replace(/^./, (c) => c.toUpperCase())}
    </span>
);

/* Only ever rendered when the record is actually breached — a green "on time"
   pill on every row would drown the handful that need attention. */
export const SlaBadge = ({ sla }) => {
    if (!sla) return null;
    const breached = sla.is_breached || sla.ack_overdue || sla.resolution_overdue;
    if (!breached) {
        const remaining = fmtRemaining(sla.resolution_hours_remaining);
        if (!remaining) return null;
        return (
            <span className={cn(chip, "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300")}>
                <Clock className="h-3 w-3" /> {remaining}
            </span>
        );
    }
    return (
        <span className={cn(chip, "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300")}>
            <AlertTriangle className="h-3 w-3" />
            {sla.ack_overdue ? "Ack overdue" : "SLA breached"}
        </span>
    );
};

export const ConfidentialityBadge = ({ confidentiality, isSensitive }) => {
    if (confidentiality === "anonymous") {
        return (
            <span className={cn(chip, "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300")}>
                <EyeOff className="h-3 w-3" /> Anonymous
            </span>
        );
    }
    if (confidentiality === "confidential") {
        return (
            <span className={cn(chip, "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300")}>
                <Lock className="h-3 w-3" /> Confidential
            </span>
        );
    }
    if (isSensitive) {
        return (
            <span className={cn(chip, "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300")}>
                <ShieldAlert className="h-3 w-3" /> Restricted
            </span>
        );
    }
    return null;
};

export const EscalationBadge = ({ level }) => {
    const stored = Number(level);
    if (!Number.isFinite(stored) || stored < 1) return null;
    const rung = displayEscalationRung(stored);
    return (
        <span className={cn(chip, "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300")}>
            L{rung} · {ESCALATION_LEVEL_LABELS[stored] || "Escalated"}
        </span>
    );
};

export const escalationLevelText = (level) => {
    const stored = Number(level);
    const safe = Number.isFinite(stored) && stored >= 0 ? stored : 0;
    return `L${displayEscalationRung(safe)} · ${ESCALATION_LEVEL_LABELS[safe] || "Handler"}`;
};

export const formatEmployeeOptionLabel = (employee) => {
    const name = (
        employee?.employee_name
        || employee?.full_name
        || employee?.display_name
        || employee?.name
        || [employee?.first_name, employee?.last_name].filter(Boolean).join(" ")
    ).trim();
    const code = String(employee?.employee_id || employee?.code || "").trim();
    if (name && code) return `${name} (${code})`;
    return name || code || "—";
};

export const employeeDropdownRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

/* -------------------------------- fragments -------------------------------- */

export const Field = ({ label, required, hint, children, className }) => (
    <div className={cn("flex flex-col gap-1.5 min-w-0", className)}>
        <label className="text-sm font-medium leading-snug text-gray-700 dark:text-gray-300">
            <span>{label}</span>
            {required && <span className="text-red-500"> *</span>}
        </label>
        <div className="min-w-0">{children}</div>
        {hint && (
            <p className="mt-1 min-h-[2.25rem] text-xs leading-relaxed text-muted-foreground">
                {hint}
            </p>
        )}
    </div>
);

export const SectionLabel = ({ children }) => (
    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {children}
    </p>
);

const renderDetailValue = (value, truncateAt) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "string" && truncateAt && value.length > truncateAt) {
        return truncateText(value, truncateAt, "400px", "balance");
    }
    return value;
};

export const DetailRows = ({ rows, truncateAt = 60 }) => (
    <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
        {rows
            .filter(([, value]) => value !== undefined)
            .map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-4 px-3 py-2">
                    <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
                    <span className="min-w-0 max-w-[65%] text-right text-xs font-medium text-gray-800 dark:text-gray-200">
                        {renderDetailValue(value, truncateAt)}
                    </span>
                </div>
            ))}
    </div>
);

const resolveAttachmentUrl = (file) => {
    if (!file) return null;
    const value = String(file).trim();
    if (!value) return null;
    // Only open a URL we know is a file link (presigned). Bare paths get
    // prefixed against the app origin and open the HRZyra website instead.
    if (/^https?:\/\//i.test(value) && /X-Amz-|Signature=|AWSAccessKeyId=/i.test(value)) {
        return value;
    }
    return null;
};

const openAttachment = async (attachment) => {
    const directUrl = resolveAttachmentUrl(attachment.file);
    if (directUrl) {
        window.open(directUrl, "_blank", "noopener,noreferrer");
        return;
    }

    try {
        const token = sessionStorage.getItem("token");
        const base = (API_URL || "").replace(/\/$/, "");
        const response = await fetch(
            `${base}/${GRIEVANCE_ATTACHMENT_DOWNLOAD}?id=${encodeURIComponent(attachment.id)}`,
            { headers: token ? { Authorization: `token ${token}` } : {} },
        );

        if (!response.ok) {
            let message = "Could not open attachment";
            try {
                const body = await response.json();
                if (body?.message) message = body.message;
            } catch (_) {
                /* not JSON */
            }
            ToastMsg("error", message);
            return;
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
        ToastMsg("error", error.message || "Could not open attachment");
    }
};

export const AttachmentList = ({ attachments }) => {
    if (!Array.isArray(attachments) || attachments.length === 0) return null;
    return (
        <div className="flex flex-col gap-1.5">
            {attachments.map((a) => (
                <button
                    key={a.id}
                    type="button"
                    onClick={() => openAttachment(a)}
                    className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-left text-sm text-sidebar-primary hover:underline dark:border-gray-700 dark:bg-gray-800"
                >
                    <Paperclip className="h-4 w-4 shrink-0" />
                    <span className="truncate">{a.original_name || "Attachment"}</span>
                    {a.visibility === "internal" && (
                        <span className="ml-auto shrink-0 text-[10px] font-semibold uppercase text-amber-600 dark:text-amber-400">
                            Internal
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
};

export const TruncatedBlock = ({ text, maxLength = 280, className }) => {
    if (!text) return null;
    if (text.length <= maxLength) {
        return (
            <p className={cn("whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-200", className)}>
                {text}
            </p>
        );
    }
    return (
        <div className={cn("text-sm leading-relaxed text-gray-700 dark:text-gray-200", className)}>
            {truncateText(text, maxLength, "480px", "balance")}
        </div>
    );
};

/* The conversation. Employee messages sit left, handler messages right, and an
   internal note is tinted so a handler can never mistake it for something the
   complainant has seen. */
export const Thread = ({ comments }) => {
    if (!Array.isArray(comments) || comments.length === 0) {
        return <p className="text-sm text-muted-foreground">No messages yet.</p>;
    }
    return (
        <div className="flex flex-col gap-2.5">
            {comments.map((c) => {
                const internal = c.visibility === "internal";
                return (
                    <div
                        key={c.id}
                        className={cn(
                            "max-w-[85%] rounded-lg border p-2.5",
                            c.is_from_employee
                                ? "self-start border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                                : "self-end border-blue-200 bg-blue-50 dark:border-blue-900/40 dark:bg-blue-900/20",
                            internal &&
                            "self-end border-amber-300 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/20",
                        )}
                    >
                        <div className="mb-1 flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                                {c.author?.name || "—"}
                            </span>
                            {internal && (
                                <span className="rounded bg-amber-200 px-1 text-[10px] font-bold uppercase text-amber-900 dark:bg-amber-900/50 dark:text-amber-200">
                                    Internal
                                </span>
                            )}
                            <span className="ml-auto text-[10px] text-muted-foreground">
                                {fmtDateTime(c.created_date)}
                            </span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                            {c.comment}
                        </p>
                    </div>
                );
            })}
        </div>
    );
};

export const Timeline = ({ entries }) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return <p className="text-sm text-muted-foreground">No activity recorded.</p>;
    }
    return (
        <ol className="relative space-y-3 border-l border-gray-200 pl-4 dark:border-gray-700">
            {entries.map((e) => (
                <li key={e.id} className="relative">
                    <span
                        className={cn(
                            "absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-gray-900",
                            e.action === "sla_breached" || e.action === "escalated"
                                ? "bg-red-500"
                                : e.action === "closed" || e.action === "resolved" || e.action === "auto_closed"
                                    ? "bg-emerald-500"
                                    : "bg-sidebar-primary",
                        )}
                    />
                    <div className="flex flex-wrap items-baseline gap-x-2">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {AUDIT_ACTION_LABELS[e.action] || e.action}
                        </p>
                        {e.is_internal && (
                            <span className="rounded bg-amber-200 px-1 text-[10px] font-bold uppercase text-amber-900 dark:bg-amber-900/50 dark:text-amber-200">
                                Internal
                            </span>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                            {e.performed_by?.name || "System"} · {fmtDateTime(e.created_date)}
                        </span>
                    </div>
                    {e.comment && (
                        <p className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">{e.comment}</p>
                    )}
                </li>
            ))}
        </ol>
    );
};

/* A dense summary header used at the top of both detail modals. */
export const GrievanceHeader = ({ grievance }) => (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3 dark:border-gray-800">
        <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                {grievance.subject}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
                {grievance.code} · {grievance.category?.name || "Uncategorised"} · raised {fmtDate(grievance.created_date)}
            </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
            <PriorityBadge priority={grievance.priority} />
            <StatusBadge status={grievance.status} resolutionType={grievance.resolution_type} />
            <EscalationBadge level={grievance.escalation_level ?? grievance.sla?.escalation_level} />
            <ConfidentialityBadge
                confidentiality={grievance.confidentiality}
                isSensitive={grievance.category?.is_sensitive}
            />
        </div>
    </div>
);
